// frontend/src/services/tagService.js
// 標籤相關的 API 服務

import api from './api';

/**
 * 取得所有標籤
 * @param {Object} options - 查詢選項
 * @param {string} options.sort - 排序欄位 (name, use_count, created_at)
 * @param {string} options.order - 排序順序 (asc, desc)
 * @param {number} options.limit - 限制數量
 * @returns {Promise<Array>} 標籤列表
 */
export const getAllTags = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.sort) params.append('sort', options.sort);
    if (options.order) params.append('order', options.order);
    if (options.limit) params.append('limit', options.limit);

    const queryString = params.toString();
    const url = queryString ? `/tags?${queryString}` : '/tags';

    const response = await api.get(url);
    // 回傳完整的 response，讓呼叫者可以取得 success 和 data
    return response.data;
  } catch (error) {
    console.error('取得標籤列表失敗:', error);
    throw error;
  }
};

/**
 * 取得單一標籤
 * @param {number} tagId - 標籤 ID
 * @returns {Promise<Object>} 標籤資料
 */
export const getTagById = async (tagId) => {
  try {
    const response = await api.get(`/tags/${tagId}`);
    return response.data.data;
  } catch (error) {
    console.error(`取得標籤 ${tagId} 失敗:`, error);
    throw error;
  }
};

/**
 * 建立新標籤
 * @param {Object} tagData - 標籤資料
 * @param {string} tagData.name - 標籤名稱（必填，1-20 字元）
 * @param {string} tagData.description - 標籤描述（選填）
 * @param {string} tagData.color - 標籤顏色（選填，hex 格式如 #1976d2）
 * @returns {Promise<Object>} 建立的標籤資料
 */
export const createTag = async (tagData) => {
  try {
    const response = await api.post('/tags', tagData);
    return response.data.data; // 回傳新建立的標籤
  } catch (error) {
    // 處理重複標籤錯誤
    if (error.response?.status === 409) {
      // 409 Conflict - 標籤已存在
      throw new Error(error.response.data.message || '標籤已存在');
    }
    console.error('建立標籤失敗:', error);
    throw error;
  }
};

/**
 * 更新標籤
 * @param {number} tagId - 標籤 ID
 * @param {Object} tagData - 要更新的標籤資料
 * @returns {Promise<Object>} 更新後的標籤資料
 */
export const updateTag = async (tagId, tagData) => {
  try {
    const response = await api.patch(`/tags/${tagId}`, tagData);
    return response.data.data;
  } catch (error) {
    console.error(`更新標籤 ${tagId} 失敗:`, error);
    throw error;
  }
};

/**
 * 刪除標籤
 * @param {number} tagId - 標籤 ID
 * @returns {Promise<void>}
 */
export const deleteTag = async (tagId) => {
  try {
    await api.delete(`/tags/${tagId}`);
  } catch (error) {
    // 處理標籤正在使用中的錯誤
    if (error.response?.status === 409) {
      throw new Error(error.response.data.message || '無法刪除正在使用的標籤');
    }
    console.error(`刪除標籤 ${tagId} 失敗:`, error);
    throw error;
  }
};

/**
 * 驗證標籤名稱格式
 * @param {string} name - 標籤名稱
 * @returns {Object} { valid: boolean, error: string }
 */
export const validateTagName = (name) => {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: '標籤名稱不可為空' };
  }

  const trimmedName = name.trim();

  if (trimmedName.length < 1 || trimmedName.length > 20) {
    return { valid: false, error: '標籤名稱長度必須在 1-20 字元之間' };
  }

  return { valid: true, error: null };
};

/**
 * 驗證標籤顏色格式
 * @param {string} color - 顏色值
 * @returns {Object} { valid: boolean, error: string }
 */
export const validateTagColor = (color) => {
  if (!color) {
    return { valid: true, error: null }; // 顏色為選填
  }

  const hexColorPattern = /^#[0-9A-Fa-f]{6}$/;
  if (!hexColorPattern.test(color)) {
    return { valid: false, error: '顏色格式錯誤（應為 hex 格式，如 #1976d2）' };
  }

  return { valid: true, error: null };
};

// 匯出 tagService 物件
export const tagService = {
  getAllTags,
  getTagById,
  createTag,
  updateTag,
  deleteTag,
  validateTagName,
  validateTagColor,
};

export default tagService;
